import { useCallback, useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { X, Flag, Check } from 'lucide-react'
import type { ChatMessage } from '../../shared/types/chat'
import { useSwipeToDismiss } from '@tendbot/ui'
import { useKeyboardHeight } from '../../hooks/useKeyboardHeight'
import { FLAG_TYPES, type FlagType } from './flagTypes'
import { reportFlag } from './reportFlag'

export interface FlagTarget {
  message: ChatMessage
  conversationId: string | null
  recentMessages: ChatMessage[]
}

interface Props {
  isOpen: boolean
  onClose: () => void
  target: FlagTarget | null
}

const PREVIEW_LIMIT = 160

export default function FlagModal({ isOpen, onClose, target }: Props) {
  const { t } = useTranslation('flag')
  const { sheetProps, backdropOpacity } = useSwipeToDismiss({ onClose })
  const { keyboardHeight, viewportHeight } = useKeyboardHeight()
  const [selected, setSelected] = useState<FlagType | null>(null)
  const [comment, setComment] = useState('')
  const [submitted, setSubmitted] = useState(false)

  // Reset shortly after close so the exit animation finishes first
  useEffect(() => {
    if (isOpen) return
    const id = window.setTimeout(() => {
      setSelected(null)
      setComment('')
      setSubmitted(false)
    }, 300)
    return () => window.clearTimeout(id)
  }, [isOpen])

  const submit = useCallback(() => {
    if (!selected || !target || submitted) return
    reportFlag({
      message: target.message,
      flagType: selected,
      comment,
      conversationId: target.conversationId,
      recentMessages: target.recentMessages,
    })
    setSubmitted(true)
    window.setTimeout(onClose, 900)
  }, [selected, comment, target, submitted, onClose])

  const rawContent = target?.message.content ?? ''
  const preview = rawContent.slice(0, PREVIEW_LIMIT)
  const truncated = rawContent.length > PREVIEW_LIMIT

  return (
    <AnimatePresence>
      {isOpen && target && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{ opacity: backdropOpacity }}
            className="fixed inset-0 bg-black/60 z-40"
          />

          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            {...sheetProps}
            style={{ height: Math.max(0, viewportHeight - 64), bottom: keyboardHeight }}
            className="fixed inset-x-0 z-50 flex flex-col"
          >
            <div className="flex-1 overflow-y-auto glass-card rounded-t-2xl border border-white/10 mx-2 flex flex-col">
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 shrink-0">
                <div className="flex items-center gap-2">
                  <Flag size={14} className="text-gold" />
                  <h2 className="font-display text-ivory text-base font-semibold">{t('title')}</h2>
                </div>
                <button
                  onClick={onClose}
                  aria-label={t('close')}
                  className="w-8 h-8 rounded-lg bg-slate-mid/50 flex items-center justify-center text-pearl/50 hover:text-ivory transition-colors"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
                <section>
                  <h3 className="text-pearl/60 text-xs font-medium uppercase tracking-wider mb-2">{t('section.flagged')}</h3>
                  <div className="glass-card rounded-lg p-3">
                    <p className="text-ivory/80 text-xs leading-relaxed break-words line-clamp-4">
                      {preview || t('noContent')}
                      {truncated && '…'}
                    </p>
                  </div>
                </section>

                <section>
                  <h3 className="text-pearl/60 text-xs font-medium uppercase tracking-wider mb-2">{t('section.reason')}</h3>
                  <div className="space-y-2">
                    {FLAG_TYPES.map(type => {
                      const active = selected === type
                      return (
                        <button
                          key={type}
                          onClick={() => setSelected(type)}
                          disabled={submitted}
                          className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors ${
                            active
                              ? 'border-gold/50 bg-gold/10'
                              : 'border-white/10 hover:border-gold/30 hover:bg-white/[0.03]'
                          } ${submitted ? 'opacity-60 cursor-not-allowed' : ''}`}
                        >
                          <span className={`w-4 h-4 mt-0.5 rounded-full border flex items-center justify-center shrink-0 ${active ? 'border-gold' : 'border-pearl/30'}`}>
                            {active && <span className="w-2 h-2 rounded-full bg-gold" />}
                          </span>
                          <span className="flex flex-col min-w-0">
                            <span className={`text-sm ${active ? 'text-ivory' : 'text-pearl/80'}`}>{t(`type.${type}.label`)}</span>
                            <span className="text-pearl/50 text-xs">{t(`type.${type}.description`)}</span>
                          </span>
                        </button>
                      )
                    })}
                  </div>
                </section>

                <section>
                  <h3 className="text-pearl/60 text-xs font-medium uppercase tracking-wider mb-2">{t('section.comment')}</h3>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    onFocus={(e) => {
                      const el = e.currentTarget
                      window.setTimeout(() => el.scrollIntoView({ block: 'center', behavior: 'smooth' }), 350)
                    }}
                    disabled={submitted}
                    placeholder={t('commentPlaceholder')}
                    rows={3}
                    maxLength={1000}
                    className="w-full bg-charcoal/60 border border-white/10 rounded-lg px-3 py-2 text-sm text-ivory placeholder:text-pearl/40 focus:outline-none focus:border-gold/30 resize-none disabled:opacity-60"
                  />
                </section>

                <section className="pb-4">
                  <button
                    onClick={submit}
                    disabled={!selected || submitted}
                    className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-colors ${
                      submitted
                        ? 'bg-emerald/15 text-emerald cursor-default'
                        : 'bg-gold text-midnight hover:bg-gold-light disabled:opacity-30 disabled:cursor-not-allowed'
                    }`}
                  >
                    {submitted ? (<><Check size={14} /> {t('submitted')}</>) : t('submit')}
                  </button>
                </section>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
